import type { LeoAskChoice } from "@/components/assessment-structure/leo-ask-prompt"
import type { LeoAssistAction } from "@/components/leo-assist-bar"
import { LEO_SWAP_ICON_CLASS } from "@/components/ui/leo-swap-icon"

/**
 * Clarifying asks for compose. Same LeoAskPrompt contract as the paper,
 * worded for one item rather than a section.
 */

export function leoComposerDraftAsk(input: {
  topic?: string
}): { question: string; choices: readonly LeoAskChoice[] } {
  const topic = input.topic?.trim()
  return {
    question: topic
      ? `How should Leo start a draft on ${topic}?`
      : "How should Leo start this question?",
    choices: [
      {
        id: "blueprint",
        label: "Follow the blueprint",
        description:
          "Match the planned category, thinking level, and question type",
        instruction: "Draft this question from the blueprint",
      },
      {
        id: "bank",
        label: "Start from the question bank",
        description: "Place an aligned bank item and leave it ready to edit",
        instruction: "Search the bank first and place a matching item",
      },
      {
        id: "mix",
        label: "Mix how students answer",
        description: "Single choice, hotspot, short answer, and more",
        instruction: "Draft this question and vary how students answer",
      },
    ],
  }
}

export function leoComposerEnhanceAsk(input: {
  issueCount?: number
}): { question: string; choices: readonly LeoAskChoice[] } {
  const issues = Math.max(0, input.issueCount ?? 0)
  const issueDetail =
    issues === 0
      ? "Rewrite only what is not aligned or needs review"
      : issues === 1
        ? "1 open issue on this question"
        : `${issues} open issues on this question`

  return {
    question: "How should Leo enhance this question?",
    choices: [
      {
        id: "fix-issues",
        label: "Fix open issues",
        description: issueDetail,
        instruction: "Only fix open issues on this question",
      },
      {
        id: "rework",
        label: "Rework the stem and choices",
        description: "Keep the blueprint cell and rewrite the wording",
        instruction: "Rework the stem and choices. Keep the blueprint cell",
      },
      {
        id: "level",
        label: "Raise the thinking level",
        description: "Write application or analysis, not recall",
        instruction: "Raise the thinking level of this question",
      },
    ],
  }
}

export function leoComposerOptionAsk(input: {
  letter: string
}): { question: string; choices: readonly LeoAskChoice[] } {
  const letter = input.letter.trim() || "this choice"
  return {
    question: `How should Leo rewrite option ${letter}?`,
    choices: [
      {
        id: "plausible",
        label: "Make it more plausible",
        description: "Keep it wrong, make it a common confusion",
        instruction: `Rewrite option ${letter} so it is a more plausible distractor`,
      },
      {
        id: "align",
        label: "Align it with the stem",
        description: "Use the same clinical language as the question",
        instruction: `Rewrite option ${letter} so it aligns with the stem`,
      },
      {
        id: "bank",
        label: "Replace it from the bank",
        description: "Pick a bank distractor for this stem",
        instruction: `Replace option ${letter} from the bank only`,
      },
    ],
  }
}

export function leoComposerChoicesAsk(): {
  question: string
  choices: readonly LeoAskChoice[]
} {
  return {
    question: "How should Leo write the answer choices?",
    choices: [
      {
        id: "stem",
        label: "Follow the stem",
        description: "One key and distractors that share the same teaching point",
        instruction: "Write answer choices that follow this stem",
      },
      {
        id: "plausible",
        label: "Mix plausible distractors",
        description: "Common errors, not throwaways",
        instruction: "Write plausible distractors for this stem",
      },
      {
        id: "bank",
        label: "Start from the question bank",
        description: "Reuse bank choices that fit this stem",
        instruction: "Write answer choices from the bank only",
      },
    ],
  }
}

/** Bank preview sheet and New question. These edit the live question. */
export const BANK_QUESTION_LEO_ACTIONS: readonly LeoAssistAction[] = [
  {
    id: "grammar",
    label: "Check grammar",
    icon: "fa-spell-check",
    instruction: "Check grammar",
  },
  {
    id: "bloom",
    label: "Change Bloom's",
    icon: "fa-graduation-cap",
    instruction: "Change Bloom's",
  },
  {
    id: "tighten",
    label: "Tighten the stem",
    icon: "fa-compress",
    instruction: "Tighten the stem",
  },
  {
    id: "difficulty",
    label: "Adjust difficulty",
    icon: "fa-chart-line",
    instruction: "Adjust difficulty",
  },
]

export const BANK_QUESTION_LEO_EXAMPLES = [
  "Check grammar",
  "Tighten the stem",
  "Add a missing rationale",
] as const

export const COMPOSER_LEO_EMPTY_ACTIONS: readonly LeoAssistAction[] = [
  {
    id: "draft",
    label: "Draft with Leo",
    icon: "fa-star-christmas",
    instruction: "Draft this question from the blueprint",
  },
  {
    id: "bank",
    label: "Start from the bank",
    icon: LEO_SWAP_ICON_CLASS,
    instruction: "Search the bank first and place a matching question",
  },
]

export const COMPOSER_LEO_REWRITE_ACTIONS: readonly LeoAssistAction[] = [
  {
    id: "enhance",
    label: "Enhance with Leo",
    icon: "fa-star-christmas",
    instruction: "Rewrite this item to match the blueprint",
  },
  {
    id: "replace",
    label: "Replace with a better match",
    icon: LEO_SWAP_ICON_CLASS,
    instruction: "Replace this item with a better match from the question bank",
  },
  {
    id: "choices",
    label: "Suggest choices",
    icon: "fa-list",
    instruction: "Write plausible answer choices for this stem",
  },
]

export const COMPOSER_LEO_EMPTY_EXAMPLES = [
  "Draft a medium Apply question on heart failure",
  "Start from the question bank",
  "Write a short-answer question on insulin",
] as const

export const COMPOSER_LEO_REWRITE_EXAMPLES = [
  "Check grammar",
  "Tighten the stem",
  "Add a missing rationale",
] as const
